import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, adminApi, appsAvailableTo, type MetaApp } from '@wa/shared';
import { ConnectCoexistence } from '@/components/ConnectCoexistence';
import { Page } from '@/components/Primitives';

/**
 * Onboarding a client: the tenant, the Meta app their traffic arrives through, and
 * their WhatsApp number.
 *
 * One page in the order the work actually happens, rather than three screens with
 * the dependencies left implicit. You cannot register a number without an app, and
 * you cannot register an app to a client that does not exist yet.
 */
export function OnboardingPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Secrets that exist in exactly one response and nowhere else afterwards.
  const [issued, setIssued] = useState<{ title: string; note: string; lines: [string, string][] } | null>(null);

  const tenants = useQuery({ queryKey: ['admin', 'tenants'], queryFn: adminApi.tenants });
  const apps = useQuery({ queryKey: ['admin', 'meta-apps'], queryFn: adminApi.metaApps });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'meta-apps'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'channels'] });
  };

  const fail = (e: unknown, fallback: string) =>
    setError(e instanceof ApiError ? e.message : fallback);

  return (
    <Page
      title="Onboarding"
      subtitle="A client, the Meta app their messages arrive through, and their WhatsApp number."
    >
      {error && (
        <p role="alert" className="mb-4 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {issued && <ShownOnce {...issued} onDismiss={() => setIssued(null)} />}

      <div className="flex flex-col gap-5">
        <TenantStep
          onDone={(t) => {
            setError(null);
            refresh();
            setIssued({
              title: `${t.companyName} is set up`,
              note: 'Send this to their administrator yourself — there is no email delivery yet, and the server keeps only a hash.',
              lines: [['Sign in as', t.administrator.email], ['Password', t.password]],
            });
          }}
          onError={(e) => fail(e, 'Could not create the client.')}
        />

        <MetaAppStep
          tenants={tenants.data ?? []}
          apps={apps.data ?? []}
          onDone={(a) => {
            setError(null);
            refresh();
            setIssued({
              title: `${a.name} is registered`,
              note: 'Paste both of these into the app’s WhatsApp webhook configuration in Meta. The verify token cannot be read back.',
              lines: [['Callback URL', `${window.location.origin.replace('admin-', 'api-')}${a.webhookPath}`],
                      ['Verify token', a.verifyToken]],
            });
          }}
          onError={(e) => fail(e, 'Could not register the Meta app.')}
        />

        <ChannelStep
          tenants={tenants.data ?? []}
          apps={apps.data ?? []}
          onDone={() => { setError(null); refresh(); }}
          onError={(e) => fail(e, 'Could not register the number.')}
        />
      </div>
    </Page>
  );
}

/** A secret in the one moment it is visible. */
function ShownOnce({ title, note, lines, onDismiss }: {
  title: string; note: string; lines: [string, string][]; onDismiss: () => void;
}) {
  return (
    <div role="status" className="mb-4 rounded-lg border border-(--color-warn) bg-(--color-note) px-3 py-3 sm:px-4">
      <p className="text-small font-semibold text-(--color-note-strong)">{title} — copy this now</p>
      <p className="mt-1 text-small text-(--color-note-ink)">{note}</p>
      <dl className="mt-2 flex flex-col gap-1.5">
        {lines.map(([label, value]) => (
          <div key={label} className="flex flex-wrap items-center gap-2">
            <dt className="w-24 shrink-0 text-micro font-medium uppercase tracking-wide text-(--color-note-strong)">{label}</dt>
            <dd className="min-w-0 flex-1">
              <code className="block truncate rounded border border-(--color-note-line) bg-(--color-surface) px-2 py-1.5 font-mono text-small">
                {value}
              </code>
            </dd>
          </div>
        ))}
      </dl>
      <button
        type="button"
        onClick={onDismiss}
        className="tap mt-2 rounded bg-(--color-note-strong) px-3 py-1.5 text-small font-medium text-white"
      >
        I have copied it
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ tenant --

function TenantStep({ onDone, onError }: {
  onDone: (t: { companyName: string; administrator: { email: string }; password: string }) => void;
  onError: (e: unknown) => void;
}) {
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [adminDisplayName, setAdminDisplayName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const create = useMutation({
    mutationFn: adminApi.createTenant,
    onSuccess: (data) => {
      onDone({ ...data, password: adminPassword });
      setCompanyName(''); setContactEmail(''); setAdminDisplayName('');
      setAdminEmail(''); setAdminPassword('');
    },
    onError,
  });

  const ready = companyName.trim().length > 0 && contactEmail.includes('@')
    && adminDisplayName.trim().length > 0 && adminEmail.includes('@') && adminPassword.length >= 12;

  return (
    <Step
      n={1}
      title="The client"
      note="Creates the company, its wallet and its first administrator together. Without an administrator nobody can sign in, so all three are written or none are."
    >
      <Grid>
        <Field label="Company name" wide>
          <Input value={companyName} onChange={setCompanyName} placeholder="Northwind Logistics" />
        </Field>
        <Field label="Contact email" wide>
          <Input value={contactEmail} onChange={setContactEmail} placeholder="ops@northwind.com" type="email" />
        </Field>
        <Field label="Administrator name" wide>
          <Input value={adminDisplayName} onChange={setAdminDisplayName} placeholder="Anita Rao" />
        </Field>
        <Field label="Administrator email" wide>
          <Input value={adminEmail} onChange={setAdminEmail} placeholder="anita@northwind.com" type="email" />
        </Field>
        <Field label="Their password" wide>
          <Input value={adminPassword} onChange={setAdminPassword} placeholder="At least 12 characters" />
        </Field>
      </Grid>
      <Submit disabled={!ready} busy={create.isPending} onClick={() => create.mutate({
        companyName: companyName.trim(), contactEmail: contactEmail.trim(),
        adminDisplayName: adminDisplayName.trim(), adminEmail: adminEmail.trim(), adminPassword,
      })}>
        Create client
      </Submit>
    </Step>
  );
}

// ---------------------------------------------------------------- meta app --

function MetaAppStep({ tenants, apps, onDone, onError }: {
  tenants: { id: number; companyName: string }[];
  apps: MetaApp[];
  onDone: (a: { name: string; webhookPath: string; verifyToken: string }) => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState('');
  const [metaAppId, setMetaAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [owner, setOwner] = useState('');

  const create = useMutation({
    mutationFn: adminApi.createMetaApp,
    onSuccess: (data) => {
      onDone(data);
      setName(''); setMetaAppId(''); setAppSecret(''); setOwner('');
    },
    onError,
  });

  return (
    <Step
      n={2}
      title="The Meta app"
      note="Skip this if the client will use PSM’s own app, which most do. Register one here only when they have brought their own Meta account — they then get their own webhook URL, verify token and app secret, so a rotated secret for one client never touches another."
    >
      {apps.length > 0 && (
        <ul className="mb-3 flex flex-col gap-1">
          {apps.map((a) => (
            <li key={a.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-small">
              <span className="font-medium">{a.name}</span>
              <span className={a.tenantId === null ? 'text-(--color-good)' : 'text-(--color-muted)'}>
                {a.tenantId === null ? 'shared by every client' : `only ${a.tenantName}`}
              </span>
              <code className="font-mono text-(--color-muted)">/{a.webhookSlug}</code>
              <span className="text-(--color-muted)">
                {a.channels} {a.channels === 1 ? 'number' : 'numbers'}
                {a.hasAppSecret ? '' : ' · no app secret'}
              </span>
              <SignupConfig app={a} onError={onError} />
            </li>
          ))}
        </ul>
      )}

      <Grid>
        <Field label="App name" wide>
          <Input value={name} onChange={setName} placeholder="Northwind’s own app" />
        </Field>
        <Field label="Meta app id" wide>
          <Input value={metaAppId} onChange={(v) => setMetaAppId(v.replace(/\D/g, ''))} placeholder="2062752177749574" />
        </Field>
        <Field label="App secret" wide>
          <Input value={appSecret} onChange={setAppSecret} placeholder="Used to check every webhook signature" />
        </Field>
        <Field label="Belongs to" wide>
          <Select value={owner} onChange={setOwner}>
            <option value="">Every client (PSM’s own app)</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>Only {t.companyName}</option>)}
          </Select>
        </Field>
      </Grid>
      <Submit
        disabled={name.trim().length === 0 || metaAppId.length === 0}
        busy={create.isPending}
        onClick={() => create.mutate({
          name: name.trim(), metaAppId,
          appSecret: appSecret.trim() || undefined,
          tenantId: owner ? Number(owner) : null,
        })}
      >
        Register app
      </Submit>
    </Step>
  );
}


/**
 * The Embedded Signup configuration id, editable in place.
 *
 * It sits on the app's row rather than in the registration form above because of
 * when it arrives: the id is created in Meta's Embedded Signup Builder, which only
 * opens once the app has been approved as a Tech Provider — weeks after somebody
 * registered the app here. A field in the creation form would be empty every time it
 * was shown, and the value would go in by hand against the database instead. Which is
 * exactly what happened until now.
 *
 * It is also the whole answer to "why is the coexistence button greyed out", so it
 * says which state it is in rather than only offering to change it.
 */
function SignupConfig({ app, onError }: { app: MetaApp; onError: (e: unknown) => void }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(app.esConfigId ?? '');

  const save = useMutation({
    mutationFn: (configId: string | null) => adminApi.updateMetaApp(app.id, { esConfigId: configId }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'meta-apps'] });
    },
    onError,
  });

  if (editing) {
    return (
      <span className="flex flex-wrap items-center gap-1">
        {/*
          The one thing worth saying at the moment somebody types this.

          Two Login configurations look identical from here — both are a run of
          digits — and only one of them was built with the WhatsApp Business App
          Onboarding feature. Paste the other and the popup does not fail: it runs
          Meta's ordinary flow and **migrates the client's number off their phone**,
          which cannot be undone from this product. PSM has already had both ids in
          play once, so this is not hypothetical.

          The server cannot check it. A configuration id is opaque, and nothing we
          are allowed to call tells us which feature it carries. So this is a
          sentence at the point of entry rather than a validation, and it says what
          goes wrong rather than what to do.
        */}
        <span className="mb-1 block w-full text-micro leading-relaxed text-(--color-warn)">
          Use the id from the configuration built with <b>WhatsApp Business App
          Onboarding</b>. A configuration without it does not fail — it migrates the
          client&rsquo;s number off their phone, and that cannot be undone from here.
        </span>
        <input
          value={value}
          autoFocus
          inputMode="numeric"
          onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
          placeholder="2255850798602464"
          className="w-44 rounded border border-(--color-line) bg-(--color-bg) px-2 py-0.5 font-mono text-small"
        />
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate(value.trim() || null)}
          className="rounded bg-(--color-brand) px-2 py-0.5 text-micro font-medium text-white disabled:opacity-40"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => { setEditing(false); setValue(app.esConfigId ?? ''); }}
          className="rounded border border-(--color-line) px-2 py-0.5 text-micro"
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`rounded border px-1.5 py-0.5 text-micro ${
        app.esConfigId
          ? 'border-(--color-line) text-(--color-muted)'
          : 'border-(--color-warn) text-(--color-warn)'
      }`}
      title={app.esConfigId
        ? 'The Embedded Signup configuration id. Coexistence connections use this app.'
        : 'Without this, a phone cannot be connected through this app — the coexistence dialog has nothing to open.'}
    >
      {app.esConfigId ? `signup ${app.esConfigId}` : 'no signup config'}
    </button>
  );
}

// ----------------------------------------------------------------- channel --

function ChannelStep({ tenants, apps, onDone, onError }: {
  tenants: { id: number; companyName: string }[];
  apps: MetaApp[];
  onDone: () => void;
  onError: (e: unknown) => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [metaAppId, setMetaAppId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [connectingPhone, setConnectingPhone] = useState(false);

  const available = appsAvailableTo(apps, tenantId ? Number(tenantId) : null);

  const check = useMutation({ mutationFn: adminApi.checkChannel });
  const create = useMutation({
    mutationFn: adminApi.createChannel,
    onSuccess: () => {
      onDone();
      setWabaId(''); setPhoneNumberId(''); setAccessToken('');
      check.reset();
    },
    onError,
  });

  const ready = tenantId && metaAppId && wabaId.trim() && phoneNumberId.trim() && accessToken.trim();
  const result = check.data;

  return (
    <Step
      n={3}
      title="Their WhatsApp number"
      note="Two ways in, and they are not interchangeable — the first question is whether the client is still answering this number on a phone."
    >
      {/*
        This fork used to be invisible, and somebody went looking for it.

        Coexistence lived only on the Channels screen, while this page is the one
        called "a client, their Meta app, and their WhatsApp number" — so the obvious
        place to add a number offered exactly one way to do it, the one that needs an
        access token. A token only exists for a number already migrated to the Cloud
        API, so following this form for a number still on a phone means failing at the
        last field with no hint that the other route exists.
      */}
      <div className="mb-4 rounded border border-(--color-line) bg-(--color-bg) px-4 py-3">
        <p className="text-small font-medium">Is the client still using this number in the WhatsApp Business app?</p>
        <p className="mt-1 text-micro leading-relaxed text-(--color-muted)">
          If they are, <b className="text-(--color-ink)">do not use the form below</b> — it needs an access
          token, which only exists once a number has been moved to the Cloud API, and
          moving it stops the app working on their phone. That cannot be undone from
          here. Connect it instead and the phone keeps working alongside us.
        </p>
        <button
          type="button"
          onClick={() => setConnectingPhone(true)}
          className="mt-2 rounded border border-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand)"
        >
          Connect a phone the client still uses
        </button>
      </div>

      {connectingPhone && (
        <ConnectCoexistence
          onClose={() => setConnectingPhone(false)}
          onConnected={() => { setConnectingPhone(false); onDone(); }}
        />
      )}

      <p className="mb-2 text-small text-(--color-muted)">
        Otherwise — the number is already on the Cloud API and you have a token for it.
        It is checked against WhatsApp before the number is saved, because a wrong token
        registers a channel that looks perfectly healthy and only fails days later as a
        customer&rsquo;s message going unanswered.
      </p>

      <Grid>
        <Field label="Client" wide>
          <Select value={tenantId} onChange={(v) => { setTenantId(v); setMetaAppId(''); }}>
            <option value="">Choose…</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.companyName}</option>)}
          </Select>
        </Field>
        <Field label="Through which app" wide>
          <Select value={metaAppId} onChange={setMetaAppId} disabled={!tenantId}>
            <option value="">Choose…</option>
            {available.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}{a.tenantId === null ? ' (shared)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="WABA id" wide>
          <Input value={wabaId} onChange={setWabaId} placeholder="970044792844079" />
        </Field>
        <Field label="Phone number id" wide>
          <Input value={phoneNumberId} onChange={setPhoneNumberId} placeholder="1249708761564274" />
        </Field>
        <Field label="Access token" wide>
          <Input value={accessToken} onChange={setAccessToken} placeholder="A permanent System User token" />
        </Field>
      </Grid>

      {result && (
        <div
          role="status"
          className={[
            'mt-2 rounded border px-3 py-2 text-small',
            result.ok
              ? 'border-(--color-good) text-(--color-good)'
              : 'border-(--color-bad) text-(--color-bad)',
          ].join(' ')}
        >
          {result.ok ? (
            <>
              WhatsApp recognises this number: <b>{result.verifiedName}</b> on{' '}
              <b>{result.displayPhoneNumber}</b>, quality {result.qualityRating ?? 'unrated'},
              sending at {result.suggestedRatePerSecond}/second.
            </>
          ) : (
            <>WhatsApp refused: {result.problem}</>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!ready || check.isPending}
          onClick={() => check.mutate({
            metaAppId: Number(metaAppId), phoneNumberId: phoneNumberId.trim(), accessToken: accessToken.trim(),
          })}
          className="tap rounded border border-(--color-line) px-3 py-1.5 text-small font-medium disabled:opacity-40"
        >
          {check.isPending ? 'Checking…' : 'Check with WhatsApp'}
        </button>

        <Submit
          disabled={!ready}
          busy={create.isPending}
          onClick={() => create.mutate({
            tenantId: Number(tenantId), metaAppId: Number(metaAppId),
            wabaId: wabaId.trim(), phoneNumberId: phoneNumberId.trim(), accessToken: accessToken.trim(),
          })}
        >
          Register number
        </Submit>

        {result && !result.ok && (
          <button
            type="button"
            disabled={!ready || create.isPending}
            onClick={() => create.mutate({
              tenantId: Number(tenantId), metaAppId: Number(metaAppId),
              wabaId: wabaId.trim(), phoneNumberId: phoneNumberId.trim(),
              accessToken: accessToken.trim(), force: true,
            })}
            title="Registers the number but leaves it switched off, so it cannot send until the token is fixed."
            className="tap rounded border border-(--color-warn) px-3 py-1.5 text-small font-medium text-(--color-warn) disabled:opacity-40"
          >
            Save anyway, switched off
          </button>
        )}
      </div>
    </Step>
  );
}

// -------------------------------------------------------------- furniture --

function Step({ n, title, note, children }: {
  n: number; title: string; note: string; children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
      <header className="border-b border-(--color-line) px-3 py-2.5 sm:px-4">
        <h2 className="flex items-baseline gap-2 text-body font-semibold">
          <span className="text-(--color-muted) tabular-nums">{n}</span>
          {title}
        </h2>
        <p className="mt-1 max-w-prose text-small leading-relaxed text-(--color-muted)">{note}</p>
      </header>
      <div className="px-3 py-3 sm:px-4">{children}</div>
    </section>
  );
}

/* Two columns where there is room, one where there is not. Every field is the
   same width, so nothing has to be measured against anything else. */
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">{children}</div>;
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <label className={['flex min-w-0 flex-col gap-1', wide ? '' : 'sm:col-span-2'].join(' ')}>
      <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</span>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-w-0 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
    />
  );
}

function Select({ value, onChange, disabled, children }: {
  value: string; onChange: (v: string) => void; disabled?: boolean; children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-full min-w-0 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small disabled:opacity-50"
    >
      {children}
    </select>
  );
}

function Submit({ disabled, busy, onClick, children }: {
  disabled: boolean; busy: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className="tap mt-3 rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
    >
      {busy ? 'Working…' : children}
    </button>
  );
}
